/**
 * Single source of truth for runtime configuration.
 *
 * All `import.meta.env.*` reads (and their fallback literals) MUST live here.
 * Feature code should import named constants from this module — never read
 * `import.meta.env` directly, and never hard-code URLs.
 *
 * Adding a new env var?
 *   1. Add the `VITE_…` key to `.env.example` with a comment.
 *   2. Export a typed constant from this file with a sensible dev default.
 *   3. Import it where needed.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const readString = (key: string, fallback: string): string => {
    const raw = import.meta.env[key] as string | undefined;
    return raw && raw.length > 0 ? raw : fallback;
};

const readBool = (key: string, fallback = false): boolean => {
    const raw = import.meta.env[key] as string | undefined;
    if (raw === undefined || raw === '') return fallback;
    return raw === 'true' || raw === '1';
};

const readNumber = (key: string, fallback: number): number => {
    const raw = import.meta.env[key] as string | undefined;
    if (raw === undefined || raw === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
};

// ---------------------------------------------------------------------------
// Environment
// ---------------------------------------------------------------------------

export const ENVIRONMENT = readString('VITE_ENVIRONMENT', 'development');
export const IS_DEV = import.meta.env.DEV;
export const IS_PROD = import.meta.env.PROD;

// ---------------------------------------------------------------------------
// Gateway / API base URLs
// ---------------------------------------------------------------------------

/** Primary Nexus/Web Gateway. All admin-portal service calls should route through here. */
export const GATEWAY_BASE_URL = readString(
    'VITE_GATEWAY_URL',
    import.meta.env.PROD ? 'https://api.koodooka.com' : 'https://api-dev.koodooka.com',
);

/** Legacy/v1 REST prefix on the gateway. */
export const API_BASE_URL = `${GATEWAY_BASE_URL}/api/v1`;

/** Dedicated Open Banking service (Tink-Link / PIS flows). */
export const OPEN_BANKING_API_URL = readString(
    'VITE_OPENBANKING_API_URL',
    'http://localhost:8000',
);

/** Open Banking admin-plane API. */
export const OPEN_BANKING_ADMIN_API_URL = readString(
    'VITE_OPEN_BANKING_ADMIN_API_URL',
    // Backwards-compat: older envs used VITE_OPEN_BANKING_API_URL for the admin plane too.
    readString('VITE_OPEN_BANKING_API_URL', 'http://localhost:8081'),
);

/** Mortgage origination service. */
export const MORTGAGE_API_URL = readString(
    'VITE_MORTGAGE_API_URL',
    readString('VITE_API_BASE_URL', `${GATEWAY_BASE_URL}/api`),
);

/** Insurance admin API — always goes through the gateway in real envs. */
export const INSURANCE_API_URL = `${GATEWAY_BASE_URL}/api/v1/insurance`;

// ---------------------------------------------------------------------------
// Auth / MSAL
// ---------------------------------------------------------------------------

export const AUTH_PROVIDER = readString('VITE_AUTH_PROVIDER', 'azure');
export const AZURE_CLIENT_ID = readString('VITE_AZURE_CLIENT_ID', '');
export const AZURE_AUTHORITY = readString('VITE_AZURE_AUTHORITY', '');
export const AZURE_TENANT_ID = readString('VITE_AZURE_TENANT_ID', 'common');
export const AZURE_REDIRECT_URI = readString('VITE_AZURE_REDIRECT_URI', 'http://localhost:3000');
export const AZURE_POST_LOGOUT_URI = readString('VITE_AZURE_POST_LOGOUT_URI', 'http://localhost:3000');
export const API_SCOPE = readString('VITE_API_SCOPE', '');

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

export const FEATURE_AUTH_REAL = readBool('VITE_FEATURE_AUTH_REAL', true);
export const FEATURE_UI_USER_MGMT = readBool('VITE_FEATURE_UI_USER_MGMT', true);
export const FEATURE_SUPER_ADMIN_UI = readBool('VITE_FEATURE_SUPER_ADMIN_UI', true);
export const ENABLE_ROLE_TESTING = readBool('VITE_ENABLE_ROLE_TESTING', false);

/**
 * Master switch for in-browser mocks (MSW-style API stubs & inline mock data).
 * Default **false** — mocks must never ship enabled in any deployed environment.
 * Override locally with `VITE_USE_MOCKS=true` in `.env.local`.
 */
export const USE_MOCKS = readBool('VITE_USE_MOCKS', false);

// ---------------------------------------------------------------------------
// Session timeout
// ---------------------------------------------------------------------------

export const SESSION_TIMEOUT_ENABLED = readBool('VITE_SESSION_TIMEOUT_ENABLED', true);
export const SESSION_TIMEOUT_MS = readNumber('VITE_SESSION_TIMEOUT', 15 * 60 * 1000);
export const SESSION_WARNING_MS = readNumber('VITE_SESSION_WARNING_TIME', 60 * 1000);

// ---------------------------------------------------------------------------
// Aggregate export (handy for debug panels / diagnostics — never log secrets)
// ---------------------------------------------------------------------------

export const env = Object.freeze({
    ENVIRONMENT,
    IS_DEV,
    IS_PROD,
    GATEWAY_BASE_URL,
    API_BASE_URL,
    OPEN_BANKING_API_URL,
    OPEN_BANKING_ADMIN_API_URL,
    MORTGAGE_API_URL,
    INSURANCE_API_URL,
    AUTH_PROVIDER,
    AZURE_CLIENT_ID,
    AZURE_AUTHORITY,
    AZURE_TENANT_ID,
    AZURE_REDIRECT_URI,
    AZURE_POST_LOGOUT_URI,
    API_SCOPE,
    FEATURE_AUTH_REAL,
    FEATURE_UI_USER_MGMT,
    FEATURE_SUPER_ADMIN_UI,
    ENABLE_ROLE_TESTING,
    USE_MOCKS,
    SESSION_TIMEOUT_ENABLED,
    SESSION_TIMEOUT_MS,
    SESSION_WARNING_MS,
});
